import React, { useState, useEffect } from 'react';
import { fetchFeedbackByCategory, fetchFeedbackDetailsByCategory } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

function StarBadge({ score }) {
  const n = Number(score ?? 0);
  if (!n) return <span style={{ color: 'var(--gray-400)' }}>—</span>;
  const color = n >= 4.5 ? '#1a8a4a' : n >= 3.5 ? '#b06a00' : '#c0392b';
  return <span style={{ fontWeight: 700, fontSize: 12, color }}>{n.toFixed(1)} ★</span>;
}

export default function FeedbackByCategory({ navigateTo }) {
  const [tab, setTab] = useState('summary');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = (p = 1) => {
    setLoading(true);
    setError(null);
    setPage(p);
    const params = {
      PageNumber: p,
      PageSize: PAGE_SIZE,
      StartDate: toApiDate(start),
      EndDate: toApiDate(end),
    };
    const fn = tab === 'summary' ? fetchFeedbackByCategory : fetchFeedbackDetailsByCategory;
    fn(params)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [tab]);

  const rows = Array.isArray(data)
    ? data
    : data?.feedbackSummaryList ?? data?.feedbackDetailsList ?? data?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Feedback by Category</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Filters</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => load(1)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
        {['summary', 'details'].map(t => (
          <button key={t} onClick={() => { setTab(t); setData(null); }} style={{ fontSize: 11, padding: '6px 16px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontWeight: 700, background: tab === t ? 'var(--navy)' : 'var(--gray-200)', color: tab === t ? '#fff' : 'var(--gray-600)' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          {tab === 'summary' ? 'Category Summary' : 'Category Details'}
          {total > 0 && ` (${total.toLocaleString()})`}
        </div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No feedback found.</div>
        )}

        {tab === 'summary' && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Count</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Product</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Packaging</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Delivery</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Service</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ease</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Overall</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 700 }}>{row.categoryName ?? '—'}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{Number(row.feedbackCount ?? 0).toLocaleString()}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.prodRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.packRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.delivRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.servRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.easeRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.overRate ?? row.overallRate} /></td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                      {row.sentFeedDate ? new Date(row.sentFeedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'details' && rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Category</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Product</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Packaging</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Delivery</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Service</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Overall</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} onClick={() => navigateTo?.('order-details', row.bcOrderId ?? row.orderId)} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId ?? row.orderId ?? '—'}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{row.categoryName ?? '—'}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.prodRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.packRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.delivRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.servRate} /></td>
                    <td style={{ padding: '8px 8px', textAlign: 'center' }}><StarBadge score={row.overRate ?? row.overallRate} /></td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>
                      {row.sentFeedDate ?? row.feedBackDate ? new Date(row.sentFeedDate ?? row.feedBackDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 && total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
            <button onClick={() => load(Math.max(1, page - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>← Prev</button>
            <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page}</span>
            <button onClick={() => load(page + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}
